import * as React from 'react';
import cn from 'classnames';

import { UploadIcon } from 'shared/newComponents/icons';
import Typography from 'shared/newComponents/Typography';
import Reference from 'shared/newComponents/Reference';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import BaseFileInput from 'shared/components/form/wrapped/BaseFileInput';
import {
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import { IconSize } from 'shared/entities/components/Icon';
import { TranslationString } from 'shared/entities/localization';
import { ReferenceProps } from 'shared/newEntities/components/Reference';
import { LoaderSpinnerColor } from 'shared/newEntities/components/LoaderSpinner';

import WithMeta from '../WithMeta';

import './FileUploadButton.modules.scss';

type Props = {
  id: string;
  label: TranslationString | string;
  className?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  accept?: string;
  error?: TranslationString | string | null;
  onChange: (file: File | null) => void;
  validate?: () => void;
  reference?: ReferenceProps;
};

const FileUploadButton: React.FC<Props> = ({
  id,
  label,
  className,
  fullWidth = false,
  disabled,
  loading,
  accept,
  error,
  onChange,
  validate,
  reference
}) => {
  const handleChange = React.useCallback(
    (files: FileList) => {
      const nextFile = files[0] ?? null;
      onChange(nextFile);
      validate?.();
    },
    [onChange, validate]
  );

  const isError = !!error;
  const isDisabled = disabled || loading;

  const buttonContent = (
    <BaseFileInput
      onChange={handleChange}
      disabled={isDisabled}
      render={({ onChange: handleInputChange }) => (
        <div
          styleName={cn(
            'file-upload-button',
            fullWidth && 'file-upload-button_full-width',
            isError && 'file-upload-button_error',
            isDisabled && 'file-upload-button_disabled',
            loading && 'file-upload-button_loading'
          )}
        >
          <label htmlFor={id} styleName="file-upload-button__label">
            {loading ? (
              <LoaderSpinner color={LoaderSpinnerColor.blue} />
            ) : (
              <>
                <UploadIcon
                  iconSize={IconSize.XM}
                  styleName="file-upload-button__icon"
                />
                <Typography
                  fontType={TypographyType.text}
                  fontSize={TypographySize.xs}
                  styleName="file-upload-button__text"
                >
                  {label}
                </Typography>
              </>
            )}
          </label>
          <input
            id={id}
            type="file"
            accept={accept}
            disabled={isDisabled}
            styleName="file-upload-button__input"
            onChange={handleInputChange}
          />
        </div>
      )}
    />
  );

  return (
    <WithMeta error={error} errorSize={TypographySize.xs} className={className}>
      {reference ? (
        <Reference {...reference} trigger={<div>{buttonContent}</div>} />
      ) : (
        buttonContent
      )}
    </WithMeta>
  );
};

export default React.memo(FileUploadButton);
